import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send } from 'lucide-react';

// Constants for form validation
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 255;
const MAX_COMPANY_LENGTH = 100;
const MAX_MESSAGE_LENGTH = 2000;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const RATE_LIMIT_MINUTES = 5;

const ChatWithAmrish = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lastSubmission, setLastSubmission] = useState<Date | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    purpose: '',
    message: ''
  });

  useEffect(() => {
    // Check for last submission time in localStorage
    const lastSubmitTime = localStorage.getItem('lastMessageSubmission');
    if (lastSubmitTime) {
      setLastSubmission(new Date(lastSubmitTime));
    }
  }, []);

  const validateForm = () => {
    // Check rate limiting
    if (lastSubmission) {
      const timeSinceLastSubmission = new Date().getTime() - lastSubmission.getTime();
      const minutesSinceLastSubmission = timeSinceLastSubmission / (1000 * 60);
      
      if (minutesSinceLastSubmission < RATE_LIMIT_MINUTES) {
        toast.error(`Please wait ${Math.ceil(RATE_LIMIT_MINUTES - minutesSinceLastSubmission)} minutes before sending another message`);
        return false;
      }
    }

    // Validate lengths
    if (formData.name.length > MAX_NAME_LENGTH) {
      toast.error('Name is too long');
      return false;
    }
    if (formData.email.length > MAX_EMAIL_LENGTH) {
      toast.error('Email is too long');
      return false;
    }
    if (formData.company.length > MAX_COMPANY_LENGTH) {
      toast.error('Company name is too long');
      return false;
    }
    if (formData.message.length > MAX_MESSAGE_LENGTH) {
      toast.error('Message is too long');
      return false;
    }

    // Validate email format
    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const sanitizeInput = (input: string): string => {
    // Remove HTML tags and trim whitespace
    return input.replace(/<[^>]*>/g, '').trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Get user information if logged in
      const { data: { user } } = await supabase.auth.getUser();

      // Sanitize all inputs
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email.toLowerCase()),
        company: sanitizeInput(formData.company),
        purpose: formData.purpose,
        message: sanitizeInput(formData.message),
        source: 'company_page'
      };

      const { error } = await supabase
        .from('contact_messages')
        .insert([sanitizedData]);

      if (error) {
        console.error('Error details:', error);
        throw error;
      }

      // Update rate limiting
      const now = new Date();
      setLastSubmission(now);
      localStorage.setItem('lastMessageSubmission', now.toISOString());

      toast.success('Message sent successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Error sending message');
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    // Prevent exceeding maximum lengths
    const maxLengths: { [key: string]: number } = {
      name: MAX_NAME_LENGTH,
      email: MAX_EMAIL_LENGTH,
      company: MAX_COMPANY_LENGTH,
      message: MAX_MESSAGE_LENGTH
    };

    if (maxLengths[field] && value.length > maxLengths[field]) {
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      <div className="container mx-auto px-4 py-8 md:py-16">
        <Button 
          variant="outline" 
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h1 className="text-3xl font-bold mb-2">Chat with Amrish</h1>
            <p className="text-gray-600 mb-8">
              Fill out this form to get in touch with Amrish. He'll get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Your Company Ltd."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose of Contact
                </label>
                <select
                  required
                  value={formData.purpose}
                  onChange={(e) => handleInputChange('purpose', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a purpose</option>
                  <option value="business_inquiry">Business Inquiry</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="investment">Investment Discussion</option>
                  <option value="general">General Question</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Message
                </label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={6}
                  placeholder="Tell me more about what you'd like to discuss..."
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-lg"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ChatWithAmrish; 